import { Injectable, inject } from "@angular/core";
import { Language } from "src/app/shared/models/language.model";
import { ProductsViewMode, User } from "../models/user.model";
import { addDays, addHours } from "../utils/functions";
import { LocalStorageService } from "./local-storage.service";
import { ThemeService } from "./theme.service";

@Injectable({ providedIn: "root" })
export class UserService {
  private localStorageService = inject(LocalStorageService);
  private themeService = inject(ThemeService);

  constructor() {
    const user = this.getUser();
    if (!user.CanRateCompany && user.CompanyRatedOn) {
      user.CanRateCompany =
        addDays(new Date(user.CompanyRatedOn), 1) < new Date();
      this.localStorageService.saveUserInfo(user);
    }

    if (user.WelcomePageLastSeenOn) {
      user.CanShowWelcomePage =
        addHours(new Date(user.WelcomePageLastSeenOn), 1) > new Date();
      this.localStorageService.saveUserInfo(user);
    } else if (!user.CanShowWelcomePage) {
      user.CanShowWelcomePage = true;
      this.localStorageService.saveUserInfo(user);
    }
  }

  getUser(): User {
    const user = this.localStorageService.getUserInfo();

    if (!user.ProductsViewMode) {
      user.ProductsViewMode = "list";
    }

    if (user && user.FontSize > 1) {
      const $html = document.querySelector("html");
      if ($html) {
        $html.style.fontSize = `${user.FontSize}%`;
      }
    }
    return user;
  }

  setUserName(name: string): User {
    const user = this.getUser();
    user.Name = name?.trim();
    this.localStorageService.saveUserInfo(user);

    return user;
  }

  setTheme(user: User, theme: string) {
    if (theme && user.Theme !== theme) {
      user.Theme = theme;
      this.localStorageService.saveUserInfo(user);

      const $html = document.querySelector("html");
      if ($html) {
        $html.classList.toggle("dark-mode", theme === "dark-mode");
      }

      this.themeService.applyParameterizationColors(
        this.localStorageService.getParameterization(),
      );
    }

    return user;
  }

  setFontSize(size: number): User {
    const user = this.getUser();

    if (size && user.FontSize !== size) {
      user.FontSize = size;
      this.localStorageService.saveUserInfo(user);

      const $html = document.querySelector("html");
      if ($html) {
        $html.style.fontSize = `${size}%`;
      }
    }

    return user;
  }

  setProductsViewMode(mode: ProductsViewMode): User {
    const user = this.getUser();

    if (mode && user.ProductsViewMode !== mode) {
      user.ProductsViewMode = mode;
      this.localStorageService.saveUserInfo(user);
    }

    return user;
  }

  setLanguage(language: Language): User {
    const user = this.getUser();

    if (language && user.Language?.Key !== language.Key) {
      user.Language = language;
      this.localStorageService.saveUserInfo(user);
    }

    return user;
  }

  getLanguage(): Language {
    const user = this.getUser();
    return user.Language;
  }

  setCompanyRated(): User {
    const user = this.getUser();
    user.CompanyRatedOn = new Date();
    user.CanRateCompany = false;
    this.localStorageService.saveUserInfo(user);

    return user;
  }

  setWelcomePageLastSeenOn(): User {
    const user = this.getUser();
    user.WelcomePageLastSeenOn = new Date();
    user.CanShowWelcomePage = false;
    this.localStorageService.saveUserInfo(user);
    return user;
  }

  resetWelcomePageLastSeenOn(): User {
    const user = this.getUser();
    user.WelcomePageLastSeenOn = null;
    user.CanShowWelcomePage = true;
    this.localStorageService.saveUserInfo(user);
    return user;
  }

  setTypeReader(isQrcode: string) {
    const user = this.getUser();
    user.isTypeQrCode = isQrcode;
    this.localStorageService.saveUserInfo(user);
  }

  getTypeReader(): string {
    const user = this.getUser();
    return user.isTypeQrCode;
  }
}
